import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { OrbitMotif } from "@/components/OrbitMotif";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import {
  ArrowRight,
  MessageSquare,
  Image as ImageIcon,
  Code2,
  FileText,
  Sparkles,
  ShieldCheck,
  Globe,
  Loader2,
  XCircle,
  CheckCircle2,
} from "lucide-react";

export default function PartnerLanding() {
  const { code } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get(`/affiliate/public/${code}`)
      .then((r) => {
        if (!active) return;
        setPartner(r.data);
        // Persist ref code so even if user goes to /pricing first, signup will pick it up
        try { localStorage.setItem("neuracom_ref", r.data.code); } catch { /* noop */ }
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [code]);

  const signupHref = partner ? `/auth?mode=signup&ref=${partner.code}` : "/auth?mode=signup";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#0B0E5C]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="mx-auto flex max-w-2xl flex-col items-center px-6 py-24 text-center" data-testid="partner-landing-not-found">
          <XCircle className="h-12 w-12 text-red-500" />
          <h1 className="mt-4 font-display text-3xl font-extrabold text-[#0B0E5C]">
            {t("partner_landing.not_found_title")}
          </h1>
          <p className="mt-2 text-slate-600">{t("partner_landing.not_found_desc")}</p>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#0B0E5C] font-bold text-white hover:bg-[#0F1370]">
            {t("partner_landing.go_home")}
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Banner: recommended by partner */}
      <div className="border-b border-[#0B0E5C]/10 bg-gradient-to-r from-[#9ACD32]/20 via-[#9ACD32]/10 to-transparent" data-testid="cobranded-banner">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 py-3 text-sm text-[#0B0E5C]">
          <CheckCircle2 className="h-4 w-4 text-[#0B0E5C]" />
          <span className="font-semibold">{t("partner_landing.recommended_by")}</span>
          <span className="font-bold" data-testid="partner-display-name">{partner.display_name}</span>
          {partner.website && (
            <a href={partner.website} target="_blank" rel="noreferrer" className="ml-1 underline opacity-70 hover:opacity-100">
              {partner.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <OrbitMotif className="pointer-events-none absolute right-0 top-0 -z-0 h-[520px] w-[520px] opacity-30" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-2 lg:py-28">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#0B0E5C]/5 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[#0B0E5C]" data-testid="cobranded-pretitle">
              <Sparkles className="h-3 w-3" />
              {t("partner_landing.hero_pretitle")}
            </span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight tracking-tight text-[#0B0E5C] sm:text-5xl lg:text-6xl" data-testid="cobranded-title">
              {t("partner_landing.hero_title")}
            </h1>
            <p className="mt-6 max-w-xl text-lg text-slate-600">
              {t("partner_landing.hero_subtitle_prefix")} <span className="font-bold text-[#0B0E5C]">{partner.display_name}</span> {t("partner_landing.hero_subtitle_suffix")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={signupHref}>
                <Button
                  size="lg"
                  className="rounded-full bg-[#9ACD32] px-7 font-bold text-[#05073B] shadow-lg hover:bg-[#A8DF38]"
                  data-testid="cobranded-cta-signup"
                >
                  {t("partner_landing.cta_primary")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="rounded-full border-[#0B0E5C]/20 px-7 font-semibold text-[#0B0E5C]" data-testid="cobranded-cta-pricing">
                  {t("partner_landing.cta_secondary")}
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#9ACD32]" /> {t("partner_landing.trust_gdpr")}</span>
              <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4 text-[#9ACD32]" /> {t("partner_landing.trust_eu")}</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#9ACD32]" /> {t("partner_landing.trust_nocard")}</span>
            </div>
          </div>

          {/* Right column: 4 feature cards */}
          <div className="grid grid-cols-2 gap-4 self-center">
            <FeatureMini icon={MessageSquare} title={t("partner_landing.feat_chat")} />
            <FeatureMini icon={ImageIcon} title={t("partner_landing.feat_image")} />
            <FeatureMini icon={Code2} title={t("partner_landing.feat_code")} />
            <FeatureMini icon={FileText} title={t("partner_landing.feat_docs")} />
          </div>
        </div>
      </section>

      {/* Trust signals / mini-case strip */}
      <section className="border-y border-slate-100 bg-slate-50/60 py-14">
        <div className="mx-auto max-w-7xl px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-slate-500">
            {t("partner_landing.results_title")}
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <ResultCard
              metric="−74%"
              label={t("partner_landing.result1_label")}
              quote={t("partner_landing.result1_quote")}
              testId="result-card-1"
            />
            <ResultCard
              metric="+3.1×"
              label={t("partner_landing.result2_label")}
              quote={t("partner_landing.result2_quote")}
              testId="result-card-2"
            />
            <ResultCard
              metric="9 min"
              label={t("partner_landing.result3_label")}
              quote={t("partner_landing.result3_quote")}
              testId="result-card-3"
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#05073B] py-20 text-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-display text-3xl font-extrabold sm:text-4xl">{t("partner_landing.cta_title")}</h2>
          <p className="mt-3 text-slate-300">{t("partner_landing.cta_subtitle")}</p>
          <Link to={signupHref}>
            <Button
              size="lg"
              className="mt-8 rounded-full bg-[#9ACD32] px-8 font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="cobranded-cta-bottom"
            >
              {t("partner_landing.cta_button")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-4 text-xs text-slate-400">{t("partner_landing.cta_attribution")} <span className="font-bold text-[#9ACD32]">{partner.display_name}</span></p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FeatureMini({ icon: Icon, title }) {
  return (
    <Card className="border-slate-200 bg-white p-5">
      <Icon className="h-6 w-6 text-[#9ACD32]" strokeWidth={2.4} />
      <p className="mt-3 text-sm font-bold text-[#0B0E5C]">{title}</p>
    </Card>
  );
}

function ResultCard({ metric, label, quote, testId }) {
  return (
    <Card className="border-slate-200 bg-white p-7" data-testid={testId}>
      <p className="font-display text-4xl font-extrabold text-[#0B0E5C]">{metric}</p>
      <p className="mt-1 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-4 text-sm leading-relaxed text-slate-600">&ldquo;{quote}&rdquo;</p>
    </Card>
  );
}
